import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ReactNode } from "react";

import { CalculationThreshold } from "./CalculationThreshold";
import { EpisodicExemptCaption } from "./EpisodicExemptCaption";
import { calculatedStatusLabel, formatLbs } from "./generatorStatusDisplay";
import { EvidenceMonth } from "./monthlyEvidence";

function CalculationRow({
  label,
  total,
  acute,
  status,
  available,
  state,
  children,
}: {
  label: string;
  total?: number;
  acute?: number;
  status?: string | null;
  available: boolean;
  state?: string | null;
  children?: ReactNode;
}) {
  return (
    <TableRow>
      <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
        {label}
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {total == null ? "Total unavailable" : `${formatLbs(total)} lb`}
        </Typography>
        {children}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {acute == null
            ? "Acute quantity unavailable"
            : `Includes ${formatLbs(acute)} lb acute waste`}
        </Typography>
      </TableCell>
      <TableCell>
        <CalculationThreshold status={status} state={state} acuteLbs={acute} totalLbs={total} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {calculatedStatusLabel(status, available)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function StateCalculationRow({
  factors,
  state,
  selected,
}: {
  factors: Record<string, number>;
  state: string | null;
  selected: EvidenceMonth | undefined;
}) {
  const stateName =
    state === "CA" ? "California" : state === "WA" ? "Washington" : (state ?? "State");
  const canExplain = state === "CA" || state === "WA";
  const acute = factors["Acute Waste (lbs)"];
  const hazardous = factors["Hazardous Waste (lbs)"];
  const additional = factors["State Regulated Waste (lbs)"];
  const federalPortion = acute != null && hazardous != null ? acute + hazardous : undefined;
  return (
    <CalculationRow
      label={stateName}
      total={canExplain ? factors["Total Hazardous Waste (lbs)"] : undefined}
      acute={canExplain ? acute : undefined}
      status={selected?.stateStatus}
      available={Boolean(selected)}
      state={state ?? "Unknown"}
    >
      {canExplain && additional != null && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {federalPortion != null && `${formatLbs(federalPortion)} lb federal + `}
          {formatLbs(additional)} lb additional {stateName} waste
        </Typography>
      )}
      {selected && (
        <EpisodicExemptCaption
          lbs={{ episodicStateRegulatedExemptLbs: selected.episodicStateRegulatedExemptLbs }}
        />
      )}
    </CalculationRow>
  );
}

export function CalculationSummary({
  federal,
  stateFactors,
  showState,
  state,
  selected,
}: {
  federal: Record<string, number>;
  stateFactors: Record<string, number>;
  showState: boolean;
  state: string | null;
  selected: EvidenceMonth | undefined;
}) {
  return (
    <TableContainer sx={{ mt: 2 }}>
      <Table
        size="small"
        aria-label="Waste and calculated status"
        sx={{
          tableLayout: "fixed",
          minWidth: 620,
          "& th, & td": { py: 1.5, verticalAlign: "top" },
          "& th:first-of-type": { pl: 0 },
          "& td:last-child, & th:last-child": { pr: 0 },
          "& td": { fontVariantNumeric: "tabular-nums" },
          "& tbody tr:last-child > *": { borderBottom: 0 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "16%" }}>Jurisdiction</TableCell>
            <TableCell sx={{ width: "32%" }}>Waste counted</TableCell>
            <TableCell sx={{ width: "34%" }}>Threshold applied</TableCell>
            <TableCell>Calculated status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <CalculationRow
            label="Federal"
            total={federal["Total Hazardous Waste (lbs)"]}
            acute={federal["Acute Waste (lbs)"]}
            status={selected?.federalStatus}
            available={Boolean(selected)}
          >
            {selected && (
              <EpisodicExemptCaption
                lbs={{
                  episodicHazardousExemptLbs: selected.episodicHazardousExemptLbs,
                  episodicAcuteExemptLbs: selected.episodicAcuteExemptLbs,
                }}
              />
            )}
          </CalculationRow>
          {showState && (
            <StateCalculationRow factors={stateFactors} state={state} selected={selected} />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
